import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import ExploreTopBar from "../components/ExploreTopBar";
// ExploreSidePanel 제거 — 기존 legacy UI 사용
import ExploreBottomBar from "../components/ExploreBottomBar";
import InfiniteScrollList from "../../../components/dataview/List";
import EstateMobileCard from "../../../components/card/SaleMobile";
import FullFilter from "../components/FullFilter";
import SingleFilterModal from "../components/SingleFilterModal";
import { buildFilterSections } from "../constants/filterSections";
import { calcBottomSheetMaxHeight } from "../constants/bottomSheetConfig";
import legacy from "../../../utils/legacy";
import styles from "./MobileLayout.module.scss";

/**
 * 모바일/태블릿 레이아웃
 *
 * 전체화면 맵 + 상단바 + 우측 사이드 패널 + 하단 "현재위치 매물" 버튼
 * 바텀시트(마커 클릭 시) + 전체필터 오버레이
 *
 * @param {Object} props
 * @param {Object}   props.filter       — useExploreFilter 반환값
 * @param {Object}   props.dataHook     — useExploreData 반환값
 * @param {Object}   props.mapHook      — useExploreMap 반환값
 * @param {React.Ref} props.sideviewRef — sideview DOM ref
 */
const MobileLayout = ({ filter, dataHook, mapHook, sideviewRef, mapInteractionRef, markerSelectRef, onSearch, searchOverlay, hidden }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [singleFilterId, setSingleFilterId] = useState(null);
  // areaUnit은 filter payload에서 관리
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false);

  // 맵 조작 시 바텀시트 닫기 + 필터 팝업 닫기
  useEffect(() => {
    if (mapInteractionRef) {
      mapInteractionRef.current = () => {
        setBottomSheetVisible(false);
        setIsFilterOpen(false);
        setSingleFilterId(null);
      };
    }
    return () => {
      if (mapInteractionRef) mapInteractionRef.current = null;
    };
  }, [mapInteractionRef]);

  // 마커 클릭 시 바텀시트 열기 + 필터 팝업 닫기
  useEffect(() => {
    if (markerSelectRef) {
      markerSelectRef.current = () => {
        setBottomSheetVisible(true);
        setIsFilterOpen(false);
        setSingleFilterId(null);
      };
    }
    return () => {
      if (markerSelectRef) markerSelectRef.current = null;
    };
  }, [markerSelectRef]);

  const { payload, handlePayload, handleResetPayload } = filter;

  // 선택된 카테고리에 따라 섹션 동적 결정 (FullFilter는 부동산 유형을 모름)
  const filterSections = useMemo(
    () => buildFilterSections({ selectedCategory: payload.selectedCategory || null }),
    [payload.selectedCategory]
  );

  const {
    data,
    detailData,
    isLoading,
    hasMore,
    listRef,
    requestLoadData,
    loadMore,
    fetchDetailList,
  } = dataHook;
  const { setIsMove, mapCount, zoomRequired, drawPolygonForEstate } = mapHook;

  // ── 네비게이션 ──

  const handleBack = useCallback(() => {
    legacy.bh?.go_before?.();
  }, []);

  const handleSearch = useCallback(() => {
    onSearch?.();
  }, [onSearch]);

  // ── 필터 ──

  const handleFullFilter = useCallback(() => {
    setIsFilterOpen(true);
  }, []);

  const topBarRef = useRef(null);
  const [chipLeft, setChipLeft] = useState(0);

  const handleChipClick = useCallback((chipId, e) => {
    if (e?.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      setChipLeft(rect.left);
    }
    setSingleFilterId(chipId);
  }, []);

  const handleFilterReset = useCallback(() => {
    handleResetPayload();
    setSingleFilterId(null);
    setIsFilterOpen(false);
  }, [handleResetPayload]);

  const handleFilterClose = useCallback(() => {
    setIsFilterOpen(false);
  }, []);

  const handleFilterApply = useCallback(() => {
    setIsFilterOpen(false);
    setIsMove(true);
  }, [setIsMove]);

  // 사이드 패널(숨김/지도/측정/분석/줌)은 기존 legacy UI 사용

  // ── 하단바 ──

  const handleBottomBarClick = useCallback(() => {
    requestLoadData();
    setIsMove(false);
    setBottomSheetVisible(true);
  }, [requestLoadData, setIsMove]);

  const handleBottomSheetClose = useCallback(() => {
    setBottomSheetVisible(false);
  }, []);

  // ── 카드 클릭 ──

  // 상세페이지 열기 + show 시 폴리곤
  const openSaleWithPolygon = useCallback(
    (suid, item) => {
      if (!suid) return;
      setIsFilterOpen(false);
      setSingleFilterId(null);
      legacy.open_sale_to_sideview(sideviewRef.current, suid, {
        mobileFullScreen: true,
        handler: {
          show: () => {
            if (item) drawPolygonForEstate(item);
          },
        },
      });
    },
    [drawPolygonForEstate, sideviewRef]
  );

  const openSaleDetail = useCallback(
    (suid) => openSaleWithPolygon(suid, null),
    [openSaleWithPolygon]
  );

  const handleCardClick = useCallback(
    (item) => {
      const suid = item.suid || item.list?.[0]?.suid;
      if (!suid) return;
      openSaleWithPolygon(suid, item);
    },
    [openSaleWithPolygon]
  );

  // ── 맵 조작 시 바텀시트 닫기 ──
  // onMapInteraction은 useExploreMap에 전달됨 (상위에서 처리)

  return (
    <div className={styles.root} ref={sideviewRef} style={hidden ? { display: "none" } : undefined}>
      {/* 상단바 */}
      <div className={styles.topBarArea} ref={topBarRef}>
        <ExploreTopBar
          onBack={handleBack}
          onSearch={handleSearch}
          payload={payload}
          onChange={handlePayload}
          onFullFilter={handleFullFilter}
          onChipClick={handleChipClick}
          onReset={handleFilterReset}
        />
      </div>

      {/* 개별 필터 — 모바일: 전체필터와 동일 위치, 태블릿: 칩 아래 좌우 위치 */}
      {singleFilterId && (
        <div className={styles.filterPopup} style={{ '--chip-left': `${chipLeft}px` }}>
          <SingleFilterModal
            filterId={singleFilterId}
            filter={payload}
            onChange={handlePayload}
            onClose={() => setSingleFilterId(null)}
            selectedCategory={payload.selectedCategory}
          />
        </div>
      )}

      {/* 맵 영역 (legacy가 이미 렌더링) — 사이드 패널 + 하단바가 위에 플로팅 */}
      <div className={styles.mapArea}>
        {/* 숨김/지도/측정/분석/줌은 기존 legacy UI 사용 */}

        <ExploreBottomBar
          count={mapCount}
          onClick={handleBottomBarClick}
          zoomRequired={zoomRequired}
        />
      </div>

      {/* 바텀시트 (마커 클릭 시) — 높이는 매물 개수에 따라 동적 결정 */}
      {bottomSheetVisible && data.list.length > 0 && (
        <BottomSheet
          items={data.list}
          totalCount={data.totalCount}
          detailData={detailData}
          fetchDetailList={fetchDetailList}
          topBarRef={topBarRef}
          listRef={listRef}
          isLoading={isLoading}
          hasMore={hasMore}
          onCardClick={handleCardClick}
          onOpenDetail={openSaleDetail}
          onLoadMore={loadMore}
          onClose={handleBottomSheetClose}
        />
      )}

      {/* 전체필터 — 팝업 */}
      {isFilterOpen && (
        <div className={styles.filterPopup}>
          <FullFilter
            sections={filterSections}
            filter={payload}
            onChange={handlePayload}
            onReset={handleFilterReset}
            onApply={handleFilterApply}
            onClose={handleFilterClose}
            resultCount={data.totalCount}
          />
        </div>
      )}

      {/* 검색 오버레이 */}
      {searchOverlay}
    </div>
  );
};

// ── 바텀시트 (매물 개수 기반 높이) ──

const DRAG_DISMISS_THRESHOLD = 80; // 이 이상 내리면 닫힘
const DRAG_VELOCITY_THRESHOLD = 0.4; // px/ms — 빠르게 내리면 거리 짧아도 닫힘

const BottomSheet = ({
  items,
  totalCount,
  detailData,
  fetchDetailList,
  topBarRef,
  listRef,
  isLoading,
  hasMore,
  onCardClick,
  onOpenDetail,
  onLoadMore,
  onClose,
}) => {
  const sheetStyle = useMemo(() => {
    const topBarHeight = topBarRef.current?.offsetHeight || 137;
    const screenHeight = window.innerHeight;
    return {
      maxHeight: calcBottomSheetMaxHeight(topBarHeight, screenHeight),
    };
  }, [topBarRef]);

  const bottomSheetContentRef = useRef(null);

  // ── 드래그 ──
  const dragRef = useRef({ startY: 0, startTime: 0, currentY: 0 });
  const sheetRef = useRef(null);

  const handleDragStart = useCallback((e) => {
    const y = e.touches[0].clientY;
    dragRef.current = { startY: y, startTime: Date.now(), currentY: y };
    if (sheetRef.current) {
      sheetRef.current.style.transition = "none";
    }
  }, []);

  const handleDragMove = useCallback((e) => {
    const y = e.touches[0].clientY;
    const deltaY = y - dragRef.current.startY;
    dragRef.current.currentY = y;
    // 위로는 안 올라감, 아래로만
    if (deltaY > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    const { startY, startTime, currentY } = dragRef.current;
    const deltaY = currentY - startY;
    const elapsed = Date.now() - startTime;
    const velocity = elapsed > 0 ? deltaY / elapsed : 0;

    if (sheetRef.current) {
      sheetRef.current.style.transition = "transform 0.25s ease-out";
    }

    if (deltaY > DRAG_DISMISS_THRESHOLD || velocity > DRAG_VELOCITY_THRESHOLD) {
      // 닫기 애니메이션: 아래로 슬라이드
      if (sheetRef.current) {
        sheetRef.current.style.transform = "translateY(100%)";
      }
      setTimeout(onClose, 250);
    } else {
      // 원위치 복귀
      if (sheetRef.current) {
        sheetRef.current.style.transform = "translateY(0)";
      }
    }
  }, [onClose]);

  return (
    <div className={styles.bottomSheet} style={sheetStyle} ref={sheetRef}>
      {/* 드래그 영역: 핸들 + 헤더 */}
      <div
        className={styles.bottomSheetDragArea}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        <div className={styles.bottomSheetHandle}>
          <div className={styles.bottomSheetBar} />
        </div>
        <div className={styles.bottomSheetHeader}>
          <span className={styles.bottomSheetCount}>
            총 {totalCount ?? items.length}개 매물
          </span>
          <button type="button" className={styles.bottomSheetClose} onClick={onClose}>
            <CloseSmallIcon />
          </button>
        </div>
      </div>

      <div className={styles.bottomSheetContent} ref={bottomSheetContentRef}>
        <InfiniteScrollList
          ref={listRef}
          items={items}
          renderItem={(d) => (
            <div data-group-key={d.groupKey}>
              <EstateMobileCard
                {...d}
                list={detailData[d.groupKey]?.data || []}
                onClick={() => onCardClick(d)}
                onOpenDetail={onOpenDetail}
                onMoreClick={() => fetchDetailList(d.groupKey)}
              />
            </div>
          )}
          onTrigger={onLoadMore}
          isLoading={isLoading}
          hasMore={hasMore}
        />
      </div>
    </div>
  );
};

const CloseSmallIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default MobileLayout;
